import './output.css';
/* global __firebase_config, __app_id, __initial_auth_token */
import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken, 
  onAuthStateChanged, 
  signOut 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query
} from 'firebase/firestore';
import { 
  Leaf, 
  LayoutDashboard, 
  Calendar, 
  Map as MapIcon, 
  ClipboardList, 
  Users, 
  Droplets, 
  RotateCw, 
  TestTube, 
  ThermometerSnowflake, 
  CloudSun, 
  CheckSquare, 
  StickyNote, 
  LogOut, 
  Plus, 
  Trash2, 
  Sprout, 
  X, 
  Layers,
  ChevronRight,
  Sun,
  Wind,
  Droplet
} from 'lucide-react';

// --- FIREBASE CONFIG & ENV CHECKS ---
const firebaseConfig = typeof __firebase_config !== 'undefined' 
  ? JSON.parse(__firebase_config) 
  : { apiKey: "fallback" };

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const appId = typeof __app_id !== 'undefined' ? __app_id : 'plotpal-pro';

const PIXELS_PER_METER = 50; 
const GRID_SIZE = 10; 

const SIDEBAR_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'guide', label: 'Monthly Guide', icon: Calendar },
  { id: 'planner', label: 'Plot Planner', icon: MapIcon },
  { id: 'schedule', label: 'Planting Schedule', icon: ClipboardList },
  { id: 'companion', label: 'Companion Guide', icon: Users },
  { id: 'harvest', label: 'Harvest Log', icon: Leaf },
  { id: 'watering', label: 'Watering', icon: Droplets },
  { id: 'rotation', label: 'Crop Rotation', icon: RotateCw },
  { id: 'soil', label: 'Soil Health', icon: TestTube },
  { id: 'frost', label: 'Frost Dates', icon: ThermometerSnowflake },
  { id: 'weather', label: 'Weather Log', icon: CloudSun },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'notes', label: 'Notes', icon: StickyNote },
];

export default function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [plotItems, setPlotItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [plotConfig, setPlotConfig] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // --- Auth & Initialization ---
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  // --- Firestore Listeners ---
  useEffect(() => {
    if (!user) return;

    // Plot Config
    const configRef = doc(db, 'artifacts', appId, 'public', 'data', 'config', 'plotSettings');
    const unsubConfig = onSnapshot(configRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPlotConfig(data);
      }
    }, (err) => console.error(err));

    // Plot Items
    const plotCol = collection(db, 'artifacts', appId, 'public', 'data', 'plot');
    const unsubPlot = onSnapshot(plotCol, (snap) => {
      setPlotItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error(err));

    // Tasks
    const tasksCol = collection(db, 'artifacts', appId, 'public', 'data', 'tasks');
    const unsubTasks = onSnapshot(tasksCol, (snap) => {
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error(err));

    return () => {
      unsubConfig();
      unsubPlot();
      unsubTasks();
    };
  }, [user]);

  const updatePlotItem = async (id, data) => {
    if (!user) return;
    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'plot', id), data);
  };

  const addPlotItem = async (typeLabel) => {
    if (!user) return;
    await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'plot'), {
      label: typeLabel,
      type: 'bed',
      color: 'bg-amber-800',
      x: 50, y: 50, w: 100, h: 150,
      crops: []
    });
  };

  // --- Dashboard View Component ---
  const DashboardView = () => (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-emerald-700 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <p className="text-emerald-100 font-medium mb-1">Today's Weather</p>
            <h2 className="text-4xl font-black flex items-center gap-3">
              Partly cloudy <ChevronRight size={28} />
            </h2>
            <p className="text-emerald-100/80 mt-2 max-w-md">
              Wet conditions today - good time to check seed stocks or tidy the shed
            </p>
            
            <div className="flex gap-8 mt-8">
              <div className="flex items-center gap-2">
                <Sun size={20} className="text-emerald-300" />
                <div className="text-xs">
                  <p className="opacity-60 uppercase font-black">Sunrise</p>
                  <p className="font-bold">07:37</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CloudSun size={20} className="text-emerald-300" />
                <div className="text-xs">
                  <p className="opacity-60 uppercase font-black">Sunset</p>
                  <p className="font-bold">17:02</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-0 flex gap-6 items-center">
            <div className="text-right">
              <p className="text-6xl font-black">5°C</p>
              <p className="text-emerald-200 font-bold">4° / 8°</p>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-xl backdrop-blur-sm">
                <Droplet size={16} /> <span className="text-sm font-bold">90%</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-xl backdrop-blur-sm">
                <Wind size={16} /> <span className="text-sm font-bold">9 km/h</span>
              </div>
            </div>
          </div>
        </div>
        <Leaf size={300} className="absolute -right-20 -bottom-20 text-white/5 rotate-12 pointer-events-none" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Plots', value: '1', sub: 'Active allotments', icon: MapIcon },
          { label: 'Beds', value: plotItems.length, sub: 'Growing areas', icon: Leaf },
          { label: 'Harvests', value: '0', sub: 'This season', icon: Sprout },
          { label: 'Pending Tasks', value: tasks.filter(t => !t.completed).length, sub: 'To complete', icon: Droplets },
        ].map((stat, i) => (
          <div key={i} className="bg-stone-50 border border-stone-200/60 p-6 rounded-2xl flex justify-between items-start group hover:bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <div>
              <p className="text-stone-400 text-xs font-black uppercase tracking-widest mb-4">{stat.label}</p>
              <p className="text-4xl font-black text-stone-800">{stat.value}</p>
              <p className="text-stone-500 text-sm mt-1">{stat.sub}</p>
            </div>
            <div className="bg-stone-100 p-3 rounded-xl text-stone-400 group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors">
              <stat.icon size={20} />
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-black text-stone-800 flex items-center gap-2">
          <Sprout className="text-emerald-600" /> Suggested Tasks
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: 'Order seeds for spring sowing', desc: 'Plan and order seeds while selection is good', priority: 'medium' },
            { title: 'Chit seed potatoes', desc: 'Start chitting potatoes in a cool, light place', priority: 'high' },
            { title: 'Prune fruit trees', desc: 'Complete dormant pruning before buds break', priority: 'medium' }
          ].map((task, i) => (
            <div key={i} className="bg-stone-50 border border-stone-200/50 p-6 rounded-2xl flex justify-between items-center group">
              <div>
                <p className="font-bold text-stone-800 mb-1">{task.title}</p>
                <p className="text-sm text-stone-500 mb-3">{task.desc}</p>
                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-md ${task.priority === 'high' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                  {task.priority}
                </span>
              </div>
              <button className="bg-emerald-800 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <Plus size={18} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const PlannerView = () => (
    <div className="h-[calc(100vh-160px)] grid grid-cols-1 lg:grid-cols-4 gap-6 animate-in fade-in zoom-in-95 duration-500">
      <div className="lg:col-span-1 space-y-4 overflow-y-auto pr-2">
        <div className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm">
          <h3 className="font-black text-xs uppercase tracking-widest text-emerald-800 mb-4">Plot Inventory</h3>
          <div className="space-y-2">
            {['Veg Bed', 'Path', 'Shed', 'Greenhouse'].map((label) => (
              <button 
                key={label}
                onClick={() => addPlotItem(label)}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-stone-50 hover:bg-emerald-600 hover:text-white transition-all group font-bold text-sm"
              >
                {label} <Plus size={16} className="text-stone-300 group-hover:text-white" />
              </button>
            ))}
          </div>
        </div>

        {editingItem && (
          <div className="bg-emerald-900 text-white p-6 rounded-3xl shadow-2xl relative">
            <button onClick={() => setEditingItem(null)} className="absolute top-4 right-4 text-emerald-300 hover:text-white"><X size={20}/></button>
            <h4 className="font-black text-xl mb-4">{editingItem.label}</h4>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-black uppercase text-emerald-400 block mb-2">Dimensions</label>
                <div className="grid grid-cols-2 gap-2">
                  <input 
                    type="number" 
                    value={editingItem.w} 
                    onChange={(e) => updatePlotItem(editingItem.id, { w: parseInt(e.target.value) })}
                    className="bg-emerald-800 border-none rounded-lg p-2 text-sm" 
                  />
                  <input 
                    type="number" 
                    value={editingItem.h} 
                    onChange={(e) => updatePlotItem(editingItem.id, { h: parseInt(e.target.value) })}
                    className="bg-emerald-800 border-none rounded-lg p-2 text-sm" 
                  />
                </div>
              </div>
              <button 
                onClick={() => { deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'plot', editingItem.id)); setEditingItem(null); }}
                className="w-full bg-red-500/20 hover:bg-red-500 text-red-200 py-3 rounded-xl font-black uppercase text-xs transition-colors"
              >
                Delete Element
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-3 bg-stone-100 rounded-[2.5rem] p-8 overflow-auto border-4 border-white shadow-inner flex items-center justify-center relative">
        <div 
          className="relative bg-white shadow-2xl transition-all"
          style={{
            width: `${(plotConfig?.width || 10) * PIXELS_PER_METER}px`,
            height: `${(plotConfig?.height || 20) * PIXELS_PER_METER}px`,
            backgroundImage: `radial-gradient(#e2e8f0 1px, transparent 1px)`,
            backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`
          }}
        >
          {plotConfig?.mapUrl && (
            <img src={plotConfig.mapUrl} alt="Map" className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-40" />
          )}

          {plotItems.map(item => (
            <div 
              key={item.id}
              className={`absolute rounded-sm border-2 transition-all cursor-move flex items-center justify-center ${editingItem?.id === item.id ? 'border-emerald-500 ring-4 ring-emerald-500/20 z-50' : 'border-white/50'} ${item.color || 'bg-stone-400'}`}
              style={{ left: `${item.x}px`, top: `${item.y}px`, width: `${item.w}px`, height: `${item.h}px` }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setEditingItem(item);
                const startX = e.clientX - item.x;
                const startY = e.clientY - item.y;
                const move = (me) => {
                  updatePlotItem(item.id, {
                    x: Math.round((me.clientX - startX) / GRID_SIZE) * GRID_SIZE,
                    y: Math.round((me.clientY - startY) / GRID_SIZE) * GRID_SIZE
                  });
                };
                const up = () => {
                  window.removeEventListener('mousemove', move);
                  window.removeEventListener('mouseup', up);
                };
                window.addEventListener('mousemove', move);
                window.addEventListener('mouseup', up);
              }}
            >
              <span className="text-[10px] font-black text-white uppercase drop-shadow-md">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-white text-stone-800 font-sans overflow-hidden">
      <aside className="w-72 bg-[#1a3424] h-full flex flex-col py-8 px-4 border-r border-emerald-900/10">
        <div className="flex items-center gap-3 px-4 mb-10 text-white">
          <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-900/40">
            <Leaf className="fill-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tighter leading-none">PlotPal</h1>
            <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest mt-0.5">Community Ver</p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto custom-scrollbar">
          {SIDEBAR_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-semibold text-sm ${
                activeTab === item.id 
                  ? 'bg-emerald-900/50 text-white shadow-sm ring-1 ring-white/10' 
                  : 'text-emerald-100/40 hover:text-emerald-100 hover:bg-white/5'
              }`}
            >
              <item.icon size={18} className={activeTab === item.id ? 'text-emerald-400' : ''} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="pt-6 border-t border-emerald-900/30 px-4">
          <div className="flex items-center gap-3 mb-6 p-2 rounded-xl bg-white/5 ring-1 ring-white/5">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-black text-xs">A</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white font-bold truncate">Allotment #1</p>
              <p className="text-[10px] text-emerald-400 truncate">Main Garden</p>
            </div>
          </div>
          <button onClick={() => signOut(auth)} className="w-full flex items-center gap-2 text-stone-400 hover:text-red-400 text-sm font-bold transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col bg-stone-50/30 overflow-hidden relative">
        <header className="h-20 flex items-center justify-between px-10 bg-transparent">
          <h2 className="text-2xl font-black text-stone-800 tracking-tight">
            {SIDEBAR_ITEMS.find(i => i.id === activeTab)?.label}
          </h2>
          <div className="flex items-center gap-4">
            <div className="px-4 py-2 bg-stone-100 rounded-full text-xs font-bold text-stone-500 border border-stone-200">
              Saturday, 7 February 2026
            </div>
          </div>
        </header>

        <section className="flex-1 px-10 pb-10 overflow-y-auto">
          {activeTab === 'dashboard' && <DashboardView />}
          {activeTab === 'planner' && <PlannerView />}
          
          {!['dashboard', 'planner'].includes(activeTab) && (
            <div className="flex flex-col items-center justify-center h-full opacity-30 text-stone-400">
              <Leaf size={64} className="mb-4 animate-pulse" />
              <p className="font-black uppercase tracking-widest text-xs">{activeTab} Module Coming Soon</p>
            </div>
          )}
        </section>
      </main>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>
  );
}
